// src/components/EmployeeChecklistModal.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  query,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import {
  FaTimes,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaCommentAlt,
  FaCalendarAlt,
  FaClipboardList,
  FaSpinner,
  FaBan,
  FaCheck,
  FaInfoCircle
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import styles from './EmployeeChecklistModal.module.css';

const EmployeeChecklistModal = ({ employee, onClose, inline = false }) => {
  const { userData } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [checklistData, setChecklistData] = useState({});
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [activeItemKey, setActiveItemKey] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Per-item form state
  const [formState, setFormState] = useState({});

  const canEdit = userData?.role === 'admin' || userData?.role === 'superadmin';

  // Fetch templates once
  const fetchTemplates = useCallback(async () => {
    try {
      const q = query(collection(db, 'checklist_templates'), orderBy('order', 'asc'));
      const snap = await getDocs(q);
      setTemplates(
        snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(t => t.isActive !== false)
      );
    } catch (err) {
      console.error('Error loading checklist templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  // Fetch employee checklist data once
  const fetchChecklistData = useCallback(async () => {
    if (!employee?.employeeId) return;
    try {
      const docRef = doc(db, 'employee_checklists', employee.employeeId);
      const snap = await getDoc(docRef);
      setChecklistData(snap.exists() ? (snap.data().items || {}) : {});
    } catch (err) {
      console.error('Error loading checklist data:', err);
    }
  }, [employee?.employeeId]);

  useEffect(() => {
    fetchTemplates();
    fetchChecklistData();
  }, [fetchTemplates, fetchChecklistData]);

  const getItemStatus = (templateId) => {
    return checklistData[templateId]?.status || 'pending';
  };

  const openForm = (templateId) => {
    if (!canEdit) return;
    const existing = checklistData[templateId] || {};
    setFormState({
      [templateId]: {
        completedDate: existing.completedDate || new Date().toISOString().split('T')[0],
        notApplicableReason: existing.notApplicableReason || '',
        comment: existing.comment || ''
      }
    });
    setActiveItemKey(activeItemKey === templateId ? null : templateId);
  };

  const saveItemStatus = async (templateId, status) => {
    const form = formState[templateId] || {};
    if (status === 'completed' && !form.completedDate) {
      toast.error('Please select a completion date.');
      return;
    }
    if (status === 'not_applicable' && !form.notApplicableReason?.trim()) {
      toast.error('Please provide a reason for Not Applicable.');
      return;
    }

    setUploading(true);
    const toastId = toast.loading('Saving...');

    try {
      const template = templates.find(t => t.id === templateId);
      const itemData = {
        templateId,
        title: template?.title || '',
        status,
        completedDate: status === 'completed' ? form.completedDate : '',
        notApplicableReason: status === 'not_applicable' ? form.notApplicableReason.trim() : '',
        comment: form.comment?.trim() || '',
        updatedBy: userData.uid,
        updatedByName: userData.name,
        updatedAt: serverTimestamp()
      };

      const docRef = doc(db, 'employee_checklists', employee.employeeId);
      const existingSnap = await getDoc(docRef);
      const existingItems = existingSnap.exists() ? (existingSnap.data().items || {}) : {};

      await setDoc(docRef, {
        employeeId: employee.employeeId,
        employeeName: employee.name,
        items: { ...existingItems, [templateId]: itemData },
        updatedAt: serverTimestamp()
      });

      // Refresh local state manually (no onSnapshot)
      setChecklistData(prev => ({ ...prev, [templateId]: itemData }));

      toast.success(
        status === 'completed' ? 'Marked as complete!' :
        status === 'not_applicable' ? 'Marked as not applicable!' :
        'Reset to pending.',
        { id: toastId }
      );
      setActiveItemKey(null);
    } catch (err) {
      console.error('Error saving checklist item:', err);
      toast.error('Failed to save. Try again.', { id: toastId });
    } finally {
      setUploading(false);
    }
  };

  const resetToPending = async (templateId) => {
    setUploading(true);
    const toastId = toast.loading('Resetting...');
    try {
      const docRef = doc(db, 'employee_checklists', employee.employeeId);
      const existingSnap = await getDoc(docRef);
      const existingItems = existingSnap.exists() ? (existingSnap.data().items || {}) : {};
      const template = templates.find(t => t.id === templateId);

      const pendingItem = {
            templateId,
            title: template?.title || '',
            status: 'pending',
            completedDate: '',
            notApplicableReason: '',
            comment: '',
            updatedBy: userData.uid,
            updatedByName: userData.name,
            updatedAt: serverTimestamp()
          };

      await setDoc(docRef, {
        employeeId: employee.employeeId,
        employeeName: employee.name,
        items: {
          ...existingItems,
          [templateId]: pendingItem
        },
        updatedAt: serverTimestamp()
      });

      // Refresh local state manually (no onSnapshot)
      setChecklistData(prev => ({ ...prev, [templateId]: pendingItem }));

      toast.success('Reset to pending.', { id: toastId });
      setActiveItemKey(null);
    } catch (err) {
      console.error('Error resetting:', err);
      toast.error('Failed to reset.', { id: toastId });
    } finally {
      setUploading(false);
    }
  };

  // Summary counts
  const totalItems = templates.length;
  const completedItems = templates.filter(t => getItemStatus(t.id) === 'completed').length;
  const naItems = templates.filter(t => getItemStatus(t.id) === 'not_applicable').length;
  const pendingItems = totalItems - completedItems - naItems;

  const statusIcon = (status) => {
    if (status === 'completed') return <FaCheckCircle className={styles.iconCompleted} />;
    if (status === 'not_applicable') return <FaBan className={styles.iconNA} />;
    return <FaClock className={styles.iconPending} />;
  };

  const statusLabel = (status) => {
    if (status === 'completed') return 'Completed';
    if (status === 'not_applicable') return 'Not Applicable';
    return 'Pending';
  };

  return (
    <div className={styles.backdrop} onClick={!inline ? onClose : undefined} style={inline ? { position: 'static', background: 'none', padding: 0 } : undefined}>
      <div className={inline ? styles.inlinePanel : styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <FaClipboardList size={24} />
            <div>
              <h2>Onboarding Checklist</h2>
              <p className={styles.subheading}>
                {employee.name} &bull; {employee.employeeId}
              </p>
            </div>
          </div>
          {!inline && (
            <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
              <FaTimes />
            </button>
          )}
        </div>

        {/* Progress Summary */}
        <div className={styles.summary}>
          <div className={`${styles.summaryCard} ${styles.cardCompleted}`}>
            <FaCheckCircle />
            <span className={styles.summaryCount}>{completedItems}</span>
            <span className={styles.summaryLabel}>Completed</span>
          </div>
          <div className={`${styles.summaryCard} ${styles.cardPending}`}>
            <FaClock />
            <span className={styles.summaryCount}>{pendingItems}</span>
            <span className={styles.summaryLabel}>Pending</span>
          </div>
          <div className={`${styles.summaryCard} ${styles.cardNA}`}>
            <FaBan />
            <span className={styles.summaryCount}>{naItems}</span>
            <span className={styles.summaryLabel}>N/A</span>
          </div>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{ width: totalItems > 0 ? `${Math.round(((completedItems + naItems) / totalItems) * 100)}%` : '0%' }}
            />
            <span className={styles.progressLabel}>
              {totalItems > 0 ? Math.round(((completedItems + naItems) / totalItems) * 100) : 0}% Done
            </span>
          </div>
        </div>

        {/* Body */}
        <div className={styles.body}>
          {loadingTemplates ? (
            <div className={styles.loadingState}>
              <FaSpinner className={styles.spinner} />
              <p>Loading checklist...</p>
            </div>
          ) : templates.length === 0 ? (
            <div className={styles.emptyState}>
              <FaClipboardList size={40} />
              <p>No checklist items configured yet.</p>
              {userData?.role === 'superadmin' && (
                <p className={styles.hint}>Go to <strong>Checklist Manager</strong> in HR Tools to add items.</p>
              )}
            </div>
          ) : (
            <ul className={styles.itemList}>
              {templates.map((template) => {
                const status = getItemStatus(template.id);
                const data = checklistData[template.id] || {};
                const isOpen = activeItemKey === template.id;
                const form = formState[template.id] || {};

                return (
                  <li key={template.id} className={`${styles.item} ${styles[`item_${status}`]}`}>
                    <div className={styles.itemHeader}>
                      <div className={styles.itemLeft}>
                        {statusIcon(status)}
                        <div className={styles.itemInfo}>
                          <span className={styles.itemTitle}>{template.title}</span>
                          {template.description && (
                            <span className={styles.itemDesc}>{template.description}</span>
                          )}
                        </div>
                      </div>
                      <div className={styles.itemRight}>
                        <span className={`${styles.statusBadge} ${styles[`badge_${status}`]}`}>
                          {statusLabel(status)}
                        </span>
                        {canEdit && (
                          <button
                            className={styles.actionToggleBtn}
                            onClick={() => openForm(template.id)}
                            disabled={uploading}
                          >
                            {isOpen ? 'Cancel' : 'Update'}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Completed info */}
                    {status === 'completed' && !isOpen && (
                      <div className={styles.completedInfo}>
                        <span><FaCalendarAlt /> Completed on: <strong>{data.completedDate || 'N/A'}</strong></span>
                        <span><FaInfoCircle /> Confirmed by: <strong>{data.updatedByName || 'N/A'}</strong></span>
                        {data.comment && <span><FaCommentAlt /> <em>{data.comment}</em></span>}
                      </div>
                    )}

                    {/* N/A info */}
                    {status === 'not_applicable' && !isOpen && (
                      <div className={styles.completedInfo}>
                        <span><FaInfoCircle /> Reason: <strong>{data.notApplicableReason || 'N/A'}</strong></span>
                        <span><FaInfoCircle /> Confirmed by: <strong>{data.updatedByName || 'N/A'}</strong></span>
                        {data.comment && <span><FaCommentAlt /> <em>{data.comment}</em></span>}
                      </div>
                    )}

                    {/* Inline update form */}
                    {isOpen && canEdit && (
                      <div className={styles.updateForm}>
                        <div className={styles.formRow}>
                          <label>Mark as:</label>
                          <div className={styles.actionBtns}>
                            <button
                              className={`${styles.btn} ${styles.btnComplete}`}
                              onClick={() => saveItemStatus(template.id, 'completed')}
                              disabled={uploading}
                            >
                              <FaCheck /> Complete
                            </button>
                            <button
                              className={`${styles.btn} ${styles.btnNA}`}
                              onClick={() => saveItemStatus(template.id, 'not_applicable')}
                              disabled={uploading}
                            >
                              <FaBan /> Not Applicable
                            </button>
                            {status !== 'pending' && (
                              <button
                                className={`${styles.btn} ${styles.btnReset}`}
                                onClick={() => resetToPending(template.id)}
                                disabled={uploading}
                              >
                                Reset to Pending
                              </button>
                            )}
                          </div>
                        </div>

                        <div className={styles.formFields}>
                          <div className={styles.formField}>
                            <label><FaCalendarAlt /> Completion Date</label>
                            <input
                              type="date"
                              value={form.completedDate || ''}
                              onChange={(e) =>
                                setFormState(prev => ({
                                  ...prev,
                                  [template.id]: { ...prev[template.id], completedDate: e.target.value }
                                }))
                              }
                            />
                          </div>

                          <div className={styles.formField}>
                            <label><FaBan /> Not Applicable Reason</label>
                            <input
                              type="text"
                              placeholder="Reason why this doesn't apply..."
                              value={form.notApplicableReason || ''}
                              onChange={(e) =>
                                setFormState(prev => ({
                                  ...prev,
                                  [template.id]: { ...prev[template.id], notApplicableReason: e.target.value }
                                }))
                              }
                            />
                          </div>

                          <div className={styles.formField}>
                            <label><FaCommentAlt /> Comment <span className={styles.optional}>(optional)</span></label>
                            <input
                              type="text"
                              placeholder="Any additional notes or remarks..."
                              value={form.comment || ''}
                              onChange={(e) =>
                                setFormState(prev => ({
                                  ...prev,
                                  [template.id]: { ...prev[template.id], comment: e.target.value }
                                }))
                              }
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          {!canEdit && (
            <p className={styles.viewOnlyNote}>
              <FaInfoCircle /> View only — admin or superadmin access required to update items.
            </p>
          )}
          {!inline && (
            <button className={styles.closeFooterBtn} onClick={onClose}>Close</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeChecklistModal;
